import {createSlice, createAsyncThunk, current} from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { setAppDialog, hideAppDialog } from "./appdialogslice";
import { 
    WIDGET_NUMERIC_CARD, WIDGET_DATE_CARD, WIDGET_GAUGE_CARD, WIDGET_GRID, isChart, WIDGET_TICKER, 
     widgetsInfo, getWidgetSettingsComponent, getWidgetDesignContextMenu, handleOnWidgetDesignContextMenuClick , defaultGridFieldSettings
} from "../../components/widgets/utils";
import { canCreateReports, canEditReports, canDeleteReports, canViewAnyReport } from "../../routing/authorizationhandlers";
import { toCamel, isNumeric, isInteger } from "../../lib/utils";
import { SITE_CONNECTION_TIMEOUT_LONG, SITE_CONNECTION_TIMEOUT_VERY_LONG } from "../../settings";
import { setCurrentUser } from "./currentuserslice";
import { setCurrentPage } from "./currentpageslice";
import { APP_TITLE } from "../../settings";
import i18next from "i18next";


const defaultDataset = { Id:0, Title: "None", Description: "", CategoryId: 0};

const defaultDataSource = { Id:0, Title: "None", TypeId: 0, ValueTypeId:0, Description: ""};

const defaultAggregateExpression = { Id:0, Title: "None", Name: "None", DataTypeId: 1, Description: ""};


const getNewWidgetId = (pageState) =>{
    let result = 0;

    for (let i in pageState.tiles)
    {
        if (pageState.tiles[i].Id < result)
           result = pageState.tiles[i].Id;
    }

    return --result;
};


const buildReportObject = (pageState) =>
{
    let result = { 
        title: pageState.reportTitle, tags: pageState.reportTags, description: pageState.reportDescription, 
        isLiveReport: pageState.isLiveReport, isFavorite: pageState.isFavorite, isSystem: pageState.isSystem, generateOnLoad: pageState.generateOnLoad, groupsIds: pageState.groupsIds,
        cardNumericUser: [], cardDateUser: [], cardGaugeUser: [], gridUser: [], genericChartUser: [], tickerUser: []
    };

    //handle if update
    if (pageState.reportId)
    {
        result.id = pageState.reportId;
        result.lastModifiedOn = pageState.lastModifiedOn;
    }

    for (let i in pageState.tiles)
    {
        let tile = pageState.tiles[i];

        let widget = {
            dataSourceId: tile.DataSourceId, 
            position: JSON.stringify(tile.defaultPosition)
        };

        const {
            id, widgetTypeId, dataSourceValueType, mode, errorText, loaded, loading, canceled,
            data, schema,
            onWidgetSelect, onWidgetUnSelect, getContextMenu, onMenuItemClick, onDoubleClick,
            onColumnReorder, onColumnResize, onColumnSettingChange, onSort, onPageChange, onGroupChange, onFilter,
             ...otherProps
        } = tile.properties;

           
        widget = {...widget, ...otherProps};

        //handle maximumRows
        if (isChart(tile.WidgetTypeId) || tile.WidgetTypeId == WIDGET_GRID || tile.WIDGET_TICKER)
        {            
            let maximumRows = widget.maximumRows;
            if (maximumRows == null || maximumRows == undefined || !isInteger(maximumRows.toString()) || parseInt(maximumRows.toString()) < 1)
            {
                let widgetInfo = widgetsInfo.find(e => e.id == tile.WidgetTypeId);
                maximumRows = widgetInfo.defaultSettings.maximumRowsMock;
                widget.maximumRows = maximumRows;
            } 
        }

        if (pageState.reportId)
            widget.id = tile.Id;

        if (tile.WidgetTypeId == WIDGET_NUMERIC_CARD)
            result.cardNumericUser.push(widget);
        else if (tile.WidgetTypeId == WIDGET_DATE_CARD)
            result.cardDateUser.push(widget);
        else if (tile.WidgetTypeId == WIDGET_GAUGE_CARD)
            result.cardGaugeUser.push(widget);
        else if (tile.WidgetTypeId == WIDGET_GRID)
            result.gridUser.push(widget);//{...widget, fields: widget.fields.map(e => ({...e, Id: e.id})));
        else if (isChart(tile.WidgetTypeId))
            result.genericChartUser.push({...widget, TypeId: widgetTypeId, sortDirectionId: otherProps.sortFieldDirectionId, legendPositionId: otherProps.legendPosition, topCount: otherProps.topCount == null || otherProps.topCount == undefined ? 1000 : (isNumeric(otherProps.topCount.toString()) ? parseInt(otherProps.topCount) : 0)});
        else if (tile.WidgetTypeId == WIDGET_TICKER)
            result.tickerUser.push(widget);
    }    

    //solve the issue of having a numeric field (ex. errorThreshold) that is string in API and bad request when converting
    for (let i in result.cardNumericUser)
    {
        if (result.cardNumericUser[i].errorThreshold != null && result.cardNumericUser[i].errorThreshold != undefined)
            result.cardNumericUser[i].errorThreshold = result.cardNumericUser[i].errorThreshold.toString();
    }

    for (let i in result.cardGaugeUser)
    {
        if (result.cardGaugeUser[i].errorThreshold != null && result.cardGaugeUser[i].errorThreshold != undefined)
            result.cardGaugeUser[i].errorThreshold = result.cardGaugeUser[i].errorThreshold.toString();
        
        if (result.cardGaugeUser[i].min !== null && result.cardGaugeUser[i].min !== undefined && result.cardGaugeUser[i].min !== "")
            result.cardGaugeUser[i].min = result.cardGaugeUser[i].min.toString();
        
        if (result.cardGaugeUser[i].max !== null && result.cardGaugeUser[i].max !== undefined && result.cardGaugeUser[i].max !== "")
            result.cardGaugeUser[i].max = result.cardGaugeUser[i].max.toString();
    }

    for (let i in result.gridUser)
    {
        if (result.gridUser[i].errorValue != null && result.gridUser[i].errorValue != undefined)
            result.gridUser[i].errorValue = result.gridUser[i].errorValue.toString();
    }

    return result;
};


const initialState = {
    pageLoaded: false, pageDisposed: true,
    processing: false, processingText: "",
    panes: [                    
        {
            //size: "30%",
            size:"17%",
            min: "17%",
            max: "17%",
            collapsible: false
        },           
        {
            min: "83%",
            collapsible: false,
        }, 
      ],
    reportId: 0, reportReferenceKey: "", reportTitle: "", reportDescription: "",  reportTags: "", reportIsInViewMode: false,
    isLiveReport: false, isFavorite: false, isDashboard: false, mainWidgetTypeId: 0, generateOnLoad: false, isPersonalized: false, isSystem: false,
    widgetsInfo: widgetsInfo,
    datasets:[],
    mocks:[],
    tiles:[],
    currentWidget: null,
    settingsFormDialogOpen: false,
    schedulePageOpen:false,
    settingsForm:{},
    toolsVisible: true,
    zoom: 1,
    groupsIds: [], groups:[],
    canCreate: false, canEdit: false, canDelete: false, canViewAnyReport: false, canView: false,
    containsWidgetsNonDeepChanges: false, containsWidgetsDeepChanges: false, containsGlobalChanges: false, 
    dbTiles:[],
    dbGlobalValues:{ reportTitle: "", reportDescription: "", generateOnLoad: false, isLiveReport: false, isFavorite: false, groupsIds: []}
};

const reportingDesignPageSlice = createSlice({
    name: "reportingDesignPage",
    initialState: initialState,
    reducers:{
        setInitialState: (state, action) =>  ({...initialState}),//no payload

        setProcessing: (state, action) => {
            //payload: {processing=true, processingText=false}, when processing is false, processingText is "" 
            const {processing=true, processingText=""} = action.payload;

            return ({...state, processing: processing, processingText: processing==false ? "" : processingText});
        },

        setPageValue: (state, action) => ({...state, [action.payload.id]: action.payload.value}),//payload: {id: , value:}

        setPanes: (state, action) => ({...state, panes: {...action.payload}}), 

        newReportPageLoaded: (state, action) => {
            //payload{Datasets: includes N/a as first item, Groups: the security groups containing {Id, Name}, currentUser}
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;
            
            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                datasets: action.payload.Datasets,
                groups: action.payload.Groups.map(e => ({Id: e.Id, Name: e.Name, Checked: false})),
                groupsIds: [],
                canCreate: canCreateReports(action.payload.currentUser), 
                canEdit: canEditReports(action.payload.currentUser), 
                canDelete: canDeleteReports(action.payload.currentUser),
                canViewAnyReport: canViewAnyReport(action.payload.currentUser),
                canView: false,
                isPersonalized: false
            };
        },

        //settings form
        setSettingsFormOuterValue: (state, action) => {
            //set a field inside settingsForm but NOT inside its formValues
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                settingsForm: {...state.settingsForm, [id]: value}
            });
        },

        setSettingsFormValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                settingsForm: {...state.settingsForm, formValues: {...state.settingsForm.formValues, [id]: value}}
            });
        },

        setSettingsFormSettingValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;
            let setInInitial = action.payload.setInInitial;

            if (!setInInitial)
                return ({
                    ...state, 
                    settingsForm: {...state.settingsForm, formValues: {...state.settingsForm.formValues, settings: {...state.settingsForm.formValues.settings, [id]: value}}}
                });
            else
                return ({
                    ...state, 
                    settingsForm: {...state.settingsForm, 
                        formValues: {...state.settingsForm.formValues, settings: {...state.settingsForm.formValues.settings, [id]: value}},
                        formInitialValues: {...state.settingsForm.formInitialValues, settings: {...state.settingsForm.formInitialValues.settings, [id]: value}}
                    }
                });
        },

        showSettingsFormNew: (state, action) => {
            //add a new widget which might happen whent the report is in new or edit mode
            //payload: widgetTypeId -> the inner widget type id for charts
            const pageState = current(state);

            //make sure settings form is not already open
            if (pageState.settingsFormDialogOpen)
                return state;

            const widgetTypeId = action.payload.widgetTypeId;

            //get the widget's default properties from its widgetsinfo entry
            let widgetInfo = widgetsInfo.find(e => e.id == widgetTypeId);
            let defaultSettings = widgetInfo.defaultSettings;
            let widgetId = getNewWidgetId(pageState);//a negative auto decrement long that guarantees no 2 widgets in new mode have the same id

            return {
                ...state, 
                currentWidget: null,
                settingsFormDialogOpen: true,
                settingsForm:{
                    mode: 1, widgetId: widgetId, widgetTypeId: widgetTypeId,  
                    dataSourceTypeId: widgetInfo.dataSourceType, dataSourceValueTypeId: widgetInfo.dataSourceValueType,  
                    dataSources: [defaultDataSource],//start with data sources combo containing only none
                    aggregateExpressions: [defaultAggregateExpression],
                    pageIndex: 0,
                    formKey:(new Date()).toString(),
                    formValues: {
                        id: widgetId, widgetTypeId: widgetTypeId, 
                        datasetId: 0, dataSourceId: 0, aggregateExpressionId: 0,
                        settings:{...defaultSettings, maximumRows: defaultSettings.maximumRowsMock, dataSourceName: null}//excludes fields, schema, data that are not needed in new mode
                    },
                    formInitialValues: {
                        id: widgetId, widgetTypeId: widgetTypeId, 
                        datasetId: 0, dataSourceId: 0, aggregateExpressionId: 0,
                        settings:{...defaultSettings, maximumRows: defaultSettings.maximumRowsMock, dataSourceName: null}//excludes fields, schema, data that are not needed in new mode
                    }
                }
            };
        },

        datasetDataSourcesLoaded: (state, action) => {
            //payload: {datasetId: , dataSources: [default+], aggregateExpressions:[defaultAggExp+]}
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            let datasetId = action.payload.datasetId;
            let dataSources = action.payload.dataSources;
            let aggregateExpressions = action.payload.aggregateExpressions;

            //no need to reset settings because the code in page will fire dataSourceReset() no matter whether the data sources were loaded successfully or not
            return {...state, 
                processing: false, processingText: "",
                settingsForm:{...state.settingsForm, 
                    dataSources: dataSources,
                    aggregateExpressions: aggregateExpressions,
                    formKey: (new Date()).toString(),
                    formValues:{...state.settingsForm.formInitialValues, datasetId: datasetId, dataSourceId: 0, aggregateExpressionId: 0, settings: {...state.settingsForm.formInitialValues.settings, fields: []}},
                    formInitialValues:{...state.settingsForm.formInitialValues, datasetId: datasetId, dataSourceId: 0, aggregateExpressionId: 0, settings: {...state.settingsForm.formValues.settings, fields: []}}
                }            
            };
        },

        dataSourceReset: (state, action) => {
            //since data source is reset => clear formValues.settings
            //is fired every time we change dataset or data source combos
            const pageState = current(state);
            const formState = pageState.settingsForm;
            const widgetTypeId = formState.widgetTypeId;

            const dataSourceId = formState.formValues.dataSourceId;
            let fields = [];
            
            if (formState.dataSourceTypeId == 2 && formState.mode == 2 && dataSourceId > 0)
            {
                //alert("FROM REPORT DESIGN PAGE SLICE LINE 293, THIS CODE HALL NEVER RUN");
                //this whole if block will never run because the dataSourceReset() action fires when we change dataset/datasource
                //and none of them is done in mode=2!!!!!
                //if widget has table data source (grid, chart or ticker) and we're editing widget and data source exists => rebuild fields
                const mock = pageState.mocks.find(e => e.dataSourceId == dataSourceId);
               
                fields = mock.Schema
                            .sort((a, b) => (a.OrderId > b.OrderId) ? 1 : -1)
                            .map(e => ({id: e.Id, name: e.Name, title: e.Title, dataTypeId:e.DataTypeId, isVisible: true, orderId: e.OrderId}));
            }

            let defaultSettings = widgetsInfo.find(e => e.id == widgetTypeId).defaultSettings;

            return {...state, 
                settingsForm: {...state.settingsForm, 
                    formKey: ((new Date())).toString(),
                    formValues: {...state.settingsForm.formValues, settings: {...defaultSettings, maximumRows: defaultSettings.maximumRowsMock, fields: fields} },
                    formInitialValues: {...state.settingsForm.formValues, settings: {...defaultSettings, maximumRows: defaultSettings.maximumRowsMock, fields: fields} },//must be like this i.e. refer to formValues
                }
            };
        },

        chartDataSourceSelected: (state, action) => {
            //invoked when we have a NEW chart or ticker and we select a data source => we get the fields either from mocks or DB
            const pageState = current(state);
            const formState = pageState.settingsForm;
            const widgetTypeId = formState.widgetTypeId;

            const dataSourceId = action.payload.dataSourceId;            
            const fields = action.payload.fields;

            const defaultSettings = widgetsInfo.find(e => e.id == widgetTypeId).defaultSettings;

            return {...state,
                processing: false,
                settingsForm: {
                    ...state.settingsForm, 
                    formKey: ((new Date())).toString(),
                    formValues: {...state.settingsForm.formValues, dataSourceId: dataSourceId, settings: {...defaultSettings, maximumRows: defaultSettings.maximumRowsMock, fields: fields} },
                    formInitialValues: {...state.settingsForm.formValues, dataSourceId: dataSourceId, settings: {...defaultSettings, maximumRows: defaultSettings.maximumRowsMock, fields: fields} },//must be like this i.e. refer to formValues
                }}
        },

        settingsFormPrevious : (state, action) => {
            if (state.settingsForm.pageIndex != 1)
                return state;
               
            return {...state, settingsForm: {...state.settingsForm, pageIndex: 0}};
        },

        settingsFormNext : (state, action) => {
            if (state.settingsForm.pageIndex != 0)
                return state;

            return {...state, settingsForm: {...state.settingsForm, pageIndex: 1}}
        },

        settingsFormSave: (state, action) => {
            //i am here => either data source data is locally in mock, or it WAS fetched from db and DataSourceMockLoaded was called before reaching here
            //which means mocks contain the data source data
            //action.payload is undefined if the data was in memory and if data was loaded from db => action.payload contains the api result
            const pageState = current(state);
            const formState = pageState.settingsForm;

            let newTile = {};
            const datasetId = formState.formValues.datasetId;
            const dataSourceId = formState.formValues.dataSourceId;
            const aggregateExpressionId = formState.formValues.aggregateExpressionId;
            const dataSourceTypeId = formState.dataSourceTypeId;
            
            let mock = null;
            
            if (dataSourceId > 0)
                mock = pageState.mocks.find(e => e.DataSourceId == dataSourceId);
            else
            {
                const dataSourceValueType = formState.dataSourceValueTypeId;
                
                if (dataSourceValueType == 4 || dataSourceValueType == 5)                   
                    mock =  pageState.mocks.find(e => e.DataSourceIsImplicit == true && e.DataSourceTypeId == 1 && (e.DataSourceValueTypeId == 4 || e.DataSourceValueTypeId == 5) && e.Schema[0].FieldId == aggregateExpressionId && e.Schema[0].FunctionId <= 0);                
                else
                    mock =  pageState.mocks.find(e => e.DataSourceIsImplicit == true && e.DataSourceTypeId == 1 && e.DataSourceValueTypeId == dataSourceValueType && e.Schema[0].FieldId == aggregateExpressionId && e.Schema[0].FunctionId <= 0);                      
            }
            
            const data = dataSourceTypeId == 1 ? mock.Value : mock.Table;
            const widgetId = formState.widgetId;
            const widgetTypeId = formState.widgetTypeId;
            const widgetInfoEntry = widgetsInfo.find(e => e.id == widgetTypeId);

            //handle maximum rows
            let maximumRows = 0;
            if (formState.widgetTypeId == WIDGET_GRID || isChart(formState.widgetTypeId) || formState.widgetTypeId == WIDGET_TICKER)
            {
                maximumRows = formState.formValues.settings.maximumRows;
                if (maximumRows == null || maximumRows == undefined || !isInteger(maximumRows.toString()) || parseInt(maximumRows.toString()) < 1)
                    maximumRows = widgetInfoEntry.defaultSettings.maximumRowsMock;
            }

            if (formState.mode == 1)//new widget
            {
                //get max tile id
                let maxExistingTileOrder = 0;

                for (var tileIndex in pageState.tiles)
                {
                    let df = pageState.tiles[tileIndex].defaultPosition.order;
                    if (df > maxExistingTileOrder)
                       maxExistingTileOrder = df;
                }

                //properties are the props to be passed to the widget to be created
                let properties = {
                    id: widgetId, widgetTypeId: widgetTypeId, 
                    dataSourceValueType: formState.dataSourceValueTypeId,
                    mode: 1, errorText: "", loaded: true,                    
                    onWidgetSelect: (props, dispatch) => dispatch(selectWidget(props.id)),
                    onWidgetUnSelect: (props, dispatch) => dispatch(unSelectWidget()),
                    getContextMenu: props => getWidgetDesignContextMenu(widgetTypeId),// widgetDesignContextMenu,
                    onMenuItemClick: handleOnWidgetDesignContextMenuClick,   
                    onDoubleClick: (id, props, dispatch) => dispatch(showSettingsForm(id)),                 
                    ...formState.formValues.settings,
                    data: dataSourceTypeId == 1 ? data : data.slice(0, maximumRows), schema: mock.Schema         
                };

                if (formState.widgetTypeId == WIDGET_GRID || isChart(formState.widgetTypeId) || formState.widgetTypeId == WIDGET_TICKER)
                    properties.maximumRows = maximumRows;


                if (formState.widgetTypeId == WIDGET_GRID)
                {
                    //if new widget => pass currentPageIndex to grid as well as fields and events handlers
                    properties.currentPageIndex = 0;                    
                    
                    properties.fields = mock.Schema.map( s => {
                        let obj = {
                            id: s.Id, name: s.Name, title: s.Title, 
                            datasetFieldId: s.FieldId, dataTypeId: s.DataTypeId, orderId: s.OrderId,
                            isVariation: false, isVariationReverse: false,
                            ...defaultGridFieldSettings                     
                        };

                        if (s.DataTypeId == 1)
                        {
                            obj.width = 100;
                            obj.hAlign = 2;       
                            obj.filter = "boolean";                     
                        }
                        else if (s.DataTypeId == 2)
                        {
                            obj.width = 120;
                            obj.hAlign = 2;
                            obj.filter = "numeric";

                            obj.thousandSeparator = 1;

                            if (s.IsDecimal)
                                obj.decimalPlaces = 2;
                            else
                                obj.decimalPlaces = 0;

                            if (s.IsPercent)
                                obj.showPercent = true;
                            else 
                                obj.showPercent = false;
                        }
                        else if (s.DataTypeId == 3)
                        {
                            obj.width = 150;
                            obj.hAlign = 1;    
                            obj.filter = "text";                        
                        }
                        else  if (s.DataTypeId == 4)
                        {
                            obj.width = 130;
                            obj.hAlign = 2; 
                            obj.filter="date";
                            obj.dateFormat = 7;                           
                        }
                        else  if (s.DataTypeId == 5)
                        {
                            obj.width = 160;
                            obj.hAlign = 2; 
                            obj.filter="date";
                            obj.dateFormat = 8;                           
                        }

                        return obj;
                    });

                    //events handlers
                    properties.onColumnReorder= (dispatch, widgetId, columns) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridColumnReorder({columns: columns}));
                    };

                    properties.onColumnResize= (dispatch, widgetId, columnId, oldWidth, newWidth, columns, end) => {
                        if (!end)
                            return;
                        
                        dispatch(selectWidget(widgetId));
                        
                        dispatch(onGridColumnResize({columnId: columnId, oldWidth: oldWidth, newWidth: newWidth, end: end}));
                    };
                    
                    properties.onColumnSettingChange= (dispatch, widgetId, fieldId, fieldName, prop, value) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridColumnSettingChange({fieldId: fieldId, fieldName: fieldName, prop: prop, value: value}));
                    };

                    properties.onSort= (dispatch, widgetId, sort) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridSort({sort: sort}));
                    };

                    properties.onPageChange = (dispatch, widgetId, page) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridPageChange({page: page}));
                    };

                    properties.onGroupChange = (dispatch, widgetId, group) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridGroupChange({group: group}));
                    };                    
                }

                newTile = { 
                    Id: formState.widgetId, WidgetTypeId: formState.widgetTypeId, DatasetId: formState.formValues.datasetId, 
                    DataSourceId: mock.DataSourceId, 
                    DataSourceTypeId: formState.dataSourceTypeId, 
                    DataSourceValueTypeId: formState.dataSourceValueTypeId, 
                    reorderable: properties.reorderable, resizable: properties.resizable,               
                    defaultPosition:{...widgetInfoEntry.defaultPosition, col:1, order: maxExistingTileOrder + 1},
                    properties: properties,
                    item: getWidgetSettingsComponent(widgetTypeId, properties),
                };

                //console.log("SS4")

                return {
                    ...state,
                    settingsFormDialogOpen: false,
                    //tiles:[...state.tiles.map(e => ({...e, defaultPosition: {...e.defaultPosition, order: e.defaultPosition.order+1}})), newTile]
                    tiles:[...state.tiles, newTile]
                };
            }
            else //mode is 2
            {            
                //the widget exists (can be read from db or still local) => in both cases its props exist in its tile entry
                let tile = pageState.tiles.find (e => e.Id == widgetId);

                const {fields, ...nonFieldsSettings} = formState.formValues.settings;
               
                //fields require special care
                let properties = {
                    ...tile.properties,
                    ...nonFieldsSettings, 
                    id: widgetId, widgetTypeId: widgetTypeId, 
                    dataSourceValueType: formState.dataSourceValueTypeId,
                    mode: 1, errorText: "", loaded: true, 
                    data: dataSourceTypeId == 1 ? data : data.slice(0, maximumRows), schema: mock.Schema
                };                    
                
                if (widgetTypeId == WIDGET_GRID || isChart(widgetTypeId) || widgetTypeId == WIDGET_TICKER)
                    properties.maximumRows = maximumRows;
                    
                //if the widget is grid or ticker => user might have shown/hidden fields => need to update fields accordinly
                //for the chart, modifying settings does not affect the fields that are somehow used in a dummy way => no need for anything
                if (formState.widgetTypeId == WIDGET_GRID && fields.filter(e => e.isVisible == true).length > 0)
                    properties.fields =  tile.properties.fields.map(f => {
                        //check every field in tile.properties.fields and if the user checked/unchecked it => set its isVisible
                        let _f = fields.find(e => e.id == f.id);

                        if (_f.isVisible)
                           return {...f, isVisible: true};
                        else
                           return {...f, isVisible: false};
                })

                
                if (formState.widgetTypeId == WIDGET_TICKER)
                    properties.fields =  [...fields];


                return {...state,
                    settingsFormDialogOpen: false,
                    tiles: state.tiles.map(tile => {
                        if (tile.Id == widgetId)
                            return {...tile, 
                                properties: {...properties},
                                item: getWidgetSettingsComponent(widgetTypeId, properties),
                                reorderable: properties.reorderable, resizable: properties.resizable
                            };
                        else
                            return tile;
                    })
                };
            }
        },

        dataSourceMockLoaded: (state, action) => {
            //payload {dataSourceId:  , mock: {}}
            const pageState = current(state);

            const dataSourceId = action.payload.dataSourceId;
            const mock = action.payload.mock;
           
            return {
                ...state,
                processing: false, processingText: "",
                mocks: [...state.mocks, {...mock}]
            };
        },

        implicitDataSourceLoaded: (state, action) => {
            //payload {aggregateExpressionId:  , mock: {}}
            const pageState = current(state);

            const aggregateExpressionId = action.payload.aggregateExpressionId;
            const mock = action.payload.mock;
            mock.Fields = [];
           
            return {
                ...state,
                processing: false, processingText: "",
                mocks: [...state.mocks, {...mock}]
            };
        },

        closeSettingsForm: (state, action) => {
            return {...state, settingsFormDialogOpen: false}
        },

        redirectToSchedule: (state, action) => { 
            debugger;
            //invoked when you double click a widget or right click it and choose select
            const pageState = current(state); 

            if (pageState.schedulePageOpen)
                return state;

            const widgetId = pageState.currentWidget ? pageState.currentWidget.Id : action.payload;
            if (!widgetId)
                return state;

            const currentWidget = pageState.tiles.find(e => e.Id == widgetId);
        
            return {
                ...state, 
                currentWidget: currentWidget,
                schedulePageOpen: true,           
            };
 
        },
        
        // closeSchedulePopup: (state, action) => {
        //     debugger;
        //     return {...state, schedulePageOpen: false}
        // },

        showSettingsForm: (state, action) => {
            //payload: widgetId
            //invoked when you double click a widget or right click it and choose select
            const pageState = current(state);
            const formState = pageState.settingsForm;

            if (pageState.settingsFormDialogOpen)
                return state;

            const widgetId = pageState.currentWidget ? pageState.currentWidget.Id : action.payload;
            if (!widgetId)
                return state;

            const currentWidget = pageState.tiles.find(e => e.Id == widgetId);
            const widgetTypeId = currentWidget.WidgetTypeId;
            const dataSource = pageState.mocks.find(e => e.DataSourceId == currentWidget.DataSourceId);

            //if chart widget => add to fields 0 None
            let _properties = {...currentWidget.properties};
            let _fields = _properties.fields ? [..._properties.fields] : [];

            //for charts only, we need to add None to the fields that will appear in the drop downs (X, Y, Secondary)
            if (isChart(widgetTypeId))// && widgetId > 0)
            {
                //remove None in order to add it in the first position AFTER sorting the fields
                _fields = _fields.filter(e => e.id > 0).sort((a,b) => (a.title <= b.title) ? -1 : 1);
                _fields.splice(0, 0, {id: 0, title: "None", name: "None"});
            }
            else if (widgetTypeId == WIDGET_GRID || widgetTypeId == WIDGET_TICKER)
                _fields = _fields.sort((a,b) => (a.title <= b.title) ? -1 : 1);

            //resolve data source name to be shown in the settings form when mode is 2
            let dataSourceTitle = dataSource.DataSourceTitle;
            if(dataSource.DataSourceTypeId == 1 &&  dataSource.DataSourceIsImplicit)
               dataSourceTitle = dataSource.Schema[0].Title;

            return {
                ...state, 
                currentWidget: currentWidget,
                settingsFormDialogOpen: true,
                settingsForm:{
                    mode: 2, widgetId: currentWidget.Id, widgetTypeId: currentWidget.WidgetTypeId, //type: currentWidget.TypeId, subType: currentWidget.SubTypeId, 
                    dataSources: [{Id: dataSource.DataSourceId, Title: dataSource.DataSourceTitle}],
                    aggregateExpressions: [defaultAggregateExpression],
                    dataSourceTypeId: dataSource.DataSourceTypeId, dataSourceValueTypeId: dataSource.DataSourceValueTypeId,
                    pageIndex: 1,
                    formKey:(new Date()).toString(),
                    formValues: {
                        id: currentWidget.Id, widgetTypeId: currentWidget.WidgetTypeId, //type: currentWidget.TypeId, subType: currentWidget.WidgetSubType, 
                        datasetId: dataSource.DatasetId, dataSourceId: dataSource.DataSourceId, 
                        settings: {...currentWidget.properties, fields: _fields, dataSourceTitle: dataSourceTitle}
                        // isChart(widgetTypeId) ? {...currentWidget.properties, fields: _fields, dataSourceTitle: dataSourceTitle} :
                        //           ((widgetTypeId == WIDGET_GRID || widgetTypeId == WIDGET_TICKER) ? 
                        //                         {...currentWidget.properties, fields: _fields,  dataSourceTitle: dataSourceTitle} 
                        //                         : {...currentWidget.properties, dataSourceTitle: dataSourceTitle})
                    },
                    formInitialValues: {
                        id: currentWidget.Id, widgetTypeId: currentWidget.WidgetTypeId, //type: currentWidget.TypeId, subType: currentWidget.WidgetSubType, 
                        datasetId: dataSource.DatasetId, dataSourceId: dataSource.DataSourceId, 
                        settings: {...currentWidget.properties, fields: _fields, dataSourceTitle: dataSourceTitle}
                    }
                        // isChart(widgetTypeId) ? {...currentWidget.properties, fields: _fields, dataSourceTitle: dataSourceTitle} :
                        //           ((widgetTypeId == WIDGET_GRID || widgetTypeId == WIDGET_TICKER) ? 
                        //                         {...currentWidget.properties, fields: _fields,  dataSourceTitle: dataSourceTitle} 
                        //                           }
                }
            };

            
        },
        //end of settings form

        existingReportPageLoaded: (state, action) => {
            //payload{data, currentUser}
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            let payload = action.payload.data;
            let currentUser = action.payload.currentUser;

            //transform mockas and widgets
            let mocks = payload.Mocks.map(item => {
                if(item.Table == "null" || item.Table == null)               
                    return item;                                
                else
                {
                    let table = JSON.parse(item.Table);

                    //need to transform every date value from string to new Date() js object
                    let dateFields = item.Schema.filter(e => (e.DataTypeId == 4 || e.DataTypeId == 5)).map(e => e.Name);
                    
                    table = table.map(row => {
                        let _row = {...row};

                        for (let f in dateFields)
                        {
                            if (_row[dateFields[f]] != null)
                                _row[dateFields[f]] = new Date(_row[dateFields[f]]);
                        }

                        return _row;
                    });

                   return {...item, Table: table};                
                }
            });

            let tiles = payload.Widgets.map(w => {
                let mock = mocks.find(e => e.DataSourceId == w.DataSourceId);

                const widgetTypeId = widgetsInfo.find(e => e.type == w.WidgetTypeId).id;

                const properties = {
                    id: w.Id, widgetTypeId: widgetTypeId, 
                    dataSourceValueType: w.DataSourceValueTypeId,
                    mode: 1, errorText: "", loaded: true, 
                    data: w.DataSourceTypeId == 1 ? mock.Value : mock.Table,
                    schema: mock.Schema,//toCamel(mock.Schema),
                    onWidgetSelect: (props, dispatch) => dispatch(selectWidget(w.Id)),
                    onWidgetUnSelect: (props, dispatch) => dispatch(unSelectWidget()),
                    getContextMenu: props => getWidgetDesignContextMenu(widgetTypeId),// widgetDesignContextMenu,
                    onMenuItemClick: handleOnWidgetDesignContextMenuClick,   
                    onDoubleClick: (id, props, dispatch) => dispatch(showSettingsForm(id)),
                    ...toCamel(w.Settings)
                };

                if (widgetTypeId == WIDGET_NUMERIC_CARD || widgetTypeId == WIDGET_DATE_CARD || widgetTypeId == WIDGET_GAUGE_CARD)
                {
                    delete properties.maximumRows;
                }


                if (widgetTypeId == WIDGET_GRID)
                {
                    properties.currentPageIndex = 0;

                    properties.fields = properties.fields.map(f => {
                        let dataType = mock.Schema.find(e => e.Name == f.name).DataTypeId;
                        if (dataType == 1)
                            return {...f, filter: "boolean"};
                        else if (dataType == 2)
                            return {...f, filter: "numeric"};
                        else if (dataType == 3)
                            return {...f, filter: "text"};
                        else if (dataType == 4 || dataType == 5)
                            return {...f, filter: "date"};
                    });

                    //events handlers
                    properties.onColumnReorder= (dispatch, widgetId, columns) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridColumnReorder({columns: columns}));
                    };

                    properties.onColumnResize= (dispatch, widgetId, columnId, oldWidth, newWidth, columns, end) => {
                        if (!end)
                            return;
                        
                        dispatch(selectWidget(widgetId));
                        
                        dispatch(onGridColumnResize({columnId: columnId, oldWidth: oldWidth, newWidth: newWidth, end: end}));
                    };
                    
                    properties.onColumnSettingChange= (dispatch, widgetId, fieldId, fieldName, prop, value) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridColumnSettingChange({fieldId: fieldId, fieldName: fieldName, prop: prop, value: value}));
                    };

                    properties.onSort= (dispatch, widgetId, sort) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridSort({sort: sort}));
                    };

                    properties.onPageChange = (dispatch, widgetId, page) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridPageChange({page: page}));
                    };

                    properties.onGroupChange = (dispatch, widgetId, group) => {
                        dispatch(selectWidget(widgetId));

                        dispatch(onGridGroupChange({group: group}));
                    };
                }

                //below is the transformation of every tile
                return {
                    Id: w.Id, WidgetTypeId: widgetTypeId, DatasetId: w.DatasetId, DataSourceId: w.DataSourceId,
                    DataSourceTypeId: w.DataSourceTypeId, DataSourceValueTypeId: w.DataSourceValueTypeId,
                    defaultPosition: JSON.parse(w.Settings.DefaultPosition), 
                    reorderable: properties.reorderable,
                    resizable: properties.resizable,
                    properties: properties,
                    item: getWidgetSettingsComponent(widgetTypeId, properties)
                };
            });

            document.title = APP_TITLE + " " + payload.Title;

            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                reportId: payload.Id, reportTitle: payload.Title, reportTags: payload.Tags, reportDescription: payload.Description, lastModifiedOn: payload.LastModifiedOn,
                isFavorite: payload.IsFavorite, isLiveReport: payload.IsLiveReport, isDashboard: payload.IsDashboard, generateOnLoad: payload.GenerateOnLoad,
                isPersonalized: payload.IsPersonalized, isSystem: payload.IsSystem,
                mainWidgetTypeId: payload.MainWidgetTypeId,
                reportReferenceKey: payload.ReferenceKey,
                zoom: 1,
                datasets: payload.Datasets,
                mocks: payload.Mocks.map(item => {
                    if(!item.Table)
                       return item;
                    else
                       return {...item, Table: JSON.parse(item.Table)};
                }),
                tiles: tiles,
                currentWidget: null,
                widgetsInfo: widgetsInfo,
                settingsFormDialogOpen: false,
                settingsForm: {},     
                groupsIds: payload.GroupsIds,
                groups: payload.Groups.map(e => ({Id: e.Id, Name: e.Name, Checked: payload.GroupsIds.find(g => g == e.Id) ? true : false})),
                canCreate: canCreateReports(currentUser), 
                canEdit: canEditReports(currentUser), 
                canDelete: canDeleteReports(currentUser),
                canViewAnyReport: canViewAnyReport(currentUser),
                canView: payload.CanViewReport,
                containsWidgetsNonDeepChanges: false, containsWidgetsDeepChanges: false, containsGlobalChanges: false,
                dbTiles: tiles.map(t => {
                    let {item, ..._t} = t;
                    let {dataSourceTitle, data, schema, currentPageIndex, getContextMenu, onColumnReorder, onColumnResize, onColumnSettingChange, onDoubleClick, onGroupChange,
                         onMenuItemClick, onPageChange, onSort, onWidgetSelect, onWidgetUnSelect, 
                         ..._properties} = t.properties;

                    _t.properties = _properties;
            
                    if (_t.properties.fields)
                    {
                        _t.properties.fields = [..._t.properties.fields];
                        
                        _t.properties.fields.sort((v1, v2) => {
                            if (v1.orderId < v2.orderId)
                                return -1;
                            else if (v1.orderId > v2.orderId)
                                return 1;
                            else
                                return 0;
                        });
                    }        

                    // if (t.WidgetTypeId == WIDGET_NUMERIC_CARD || t.WidgetTypeId == WIDGET_DATE_CARD || t.WidgetTypeId == WIDGET_GAUGE_CARD)
                    //    _t.properties.maximumRows = 0;

                    return ({Id: t.Id, Tile: _t});
                }),
                dbGlobalValues: {
                    reportTitle: payload.Title, reportDescription: payload.Description, generateOnLoad: payload.GenerateOnLoad,
                    isFavorite: payload.IsFavorite, isLiveReport: payload.IsLiveReport,
                    groupsIds: payload.GroupsIds
                }                     
            };
        },

        selectWidget: (state, action) => {
            //payload: id
            const pageState = current(state);
            const id = action.payload;
            const widget = pageState.tiles.find(e => e.Id == id);

            return {...state, currentWidget: widget};
        },

        unSelectWidget: (state, action) => {
            const pageState = current(state);
            const widget = pageState.currentWidget;

            if(!widget)
                return state;

            return {...state, currentWidget: null};
        },

        widgetRemoved: (state, action) => {
            //payload: id
            const pageState = current(state);

            let id = action.payload;
            let removedOrder = pageState.tiles.find(e => e.Id == id).defaultPosition.order;

            return {
                ...state,
                currentWidget: null,
                tiles: state.tiles.filter(e => e.Id != id).map(e => {
                    if (e.defaultPosition.order > removedOrder)
                        return {...e, defaultPosition: {...e.defaultPosition, order: e.defaultPosition.order -1}};
                    else
                        return e;
                })
            };
        },

        entityUpdated: (state, action) => {
            let {LastModifiedOn, CardNumericUserIds, CardDateUserIds, CardGaugeUserIds, GridUserIds, GenericChartUserIds, TickerUserIds, ...other} = action.payload;

            const pageState = current(state);

            document.title = APP_TITLE + " " + pageState.reportTitle;

            return {
                ...state,
                lastModifiedOn: LastModifiedOn,
                isPersonalized: true,
                tiles: pageState.tiles.map(e => {
                    let newId = 0;
                    if (e.Id < 0)
                    {
                        if (e.WidgetTypeId == WIDGET_NUMERIC_CARD)                        
                            newId = CardNumericUserIds.find (w => w.OldId == e.Id).NewId;
                        else if (e.WidgetTypeId == WIDGET_DATE_CARD)                        
                            newId = CardDateUserIds.find (w => w.OldId == e.Id).NewId;
                        else if (e.WidgetTypeId == WIDGET_GAUGE_CARD)                        
                            newId = CardGaugeUserIds.find (w => w.OldId == e.Id).NewId;
                        else if (e.WidgetTypeId == WIDGET_GRID)                        
                            newId = GridUserIds.find (w => w.OldId == e.Id).NewId;
                        else if (isChart(e.WidgetTypeId))                        
                            newId = GenericChartUserIds.find (w => w.OldId == e.Id).NewId;
                        else if (e.WidgetTypeId == WIDGET_TICKER)                        
                            newId = TickerUserIds.find (w => w.OldId == e.Id).NewId;
                        
                        let properties = {...e.properties, id: newId};

                        return {...e, Id: newId, properties: properties, item: getWidgetSettingsComponent(e.WidgetTypeId, properties)};
                    }
                    else
                        return e;
                }),
                containsWidgetsNonDeepChanges: false, containsWidgetsDeepChanges: false, containsGlobalChanges: false,
                dbTiles: pageState.tiles.map(t => {
                    let id = t.Id;
                    let {item, ..._t} = t;
                    let {dataSourceTitle, data, schema, currentPageIndex, getContextMenu, onColumnReorder, onColumnResize, onColumnSettingChange, onDoubleClick, onGroupChange,
                        onMenuItemClick, onPageChange, onSort, onWidgetSelect, onWidgetUnSelect,
                         ..._properties} = t.properties;

                    if (id < 0)
                    {
                        if (t.WidgetTypeId == WIDGET_NUMERIC_CARD)                        
                            id = CardNumericUserIds.find (w => w.OldId == t.Id).NewId;
                        else if (t.WidgetTypeId == WIDGET_DATE_CARD)                        
                            id = CardDateUserIds.find (w => w.OldId == t.Id).NewId;
                        else if (t.WidgetTypeId == WIDGET_GAUGE_CARD)                        
                            id = CardGaugeUserIds.find (w => w.OldId == t.Id).NewId;
                        else if (t.WidgetTypeId == WIDGET_GRID)                        
                            id = GridUserIds.find (w => w.OldId == t.Id).NewId;
                        else if (isChart(t.WidgetTypeId))                        
                            id = GenericChartUserIds.find (w => w.OldId == t.Id).NewId;
                        else if (t.WidgetTypeId == WIDGET_TICKER)                        
                            id = TickerUserIds.find (w => w.OldId == t.Id).NewId;
                    }
                    _t.Id = id;
                    _properties.id = id;
                    
                    _t.properties = _properties;

                    if (_t.properties.fields)
                    {
                        _t.properties.fields = [..._t.properties.fields];
                        
                        _t.properties.fields.sort((v1, v2) => {
                                if (v1.orderId < v2.orderId)
                                    return -1;
                                else if (v1.orderId > v2.orderId)
                                    return 1;
                                else
                                    return 0;
                        });      
                    }         

                    // if (t.WidgetTypeId == WIDGET_NUMERIC_CARD || t.WidgetTypeId == WIDGET_DATE_CARD || t.WidgetTypeId == WIDGET_GAUGE_CARD)
                    //    _t.properties.maximumRows = 0;

                    return ({Id: id, Tile: _t});
                }),
                dbGlobalValues: {
                    reportTitle: pageState.reportTitle, reportDescription: pageState.reportDescription, generateOnLoad: pageState.generateOnLoad,
                    isFavorite: pageState.isFavorite, isLiveReport: pageState.isLiveReport,
                    groupsIds: pageState.groupsIds
                } 
            };            
        },

        resetTileWidget: (state, action) => {
            //redraws the widget - for example after mouse up of resize tile
            const pageState = current(state);
            let tileId = action.payload;

            return {
                ...state,
                tiles: pageState.tiles.map(
                    t => {
                        if (t.Id != tileId)
                            return {...t};
                        else                        
                            return {...t, item: getWidgetSettingsComponent(t.WidgetTypeId, {...t.properties, redrawProp: (new Date().toString())})}                        
                    }
                )
            };
        },

        redrawTiles: (state, action) => {
            const pageState = current(state);

            let _tiles = pageState.tiles.map(t => {
                return {...t, item: getWidgetSettingsComponent(t.WidgetTypeId, {...t.properties, redrawProp: (new Date().toString())})}                        
            });

            return {...state, tiles: _tiles};
        },

        zoomIn: (state, action) => {
            const pageState = current(state);
            let zoom = pageState.zoom;
            let result = 0;

            if (zoom == 0.25)
                result = 0.5;
            else if (zoom == 0.5)
                result = 0.75;
            else if (zoom == 0.75)
                result = 1;
            else if (zoom == 1)
                result = 1.25;
            else if (zoom == 1.25)
                result = 1.5;
            else if (zoom == 1.5)
                result = 1.75;
            else //if (zoom == 1.75)
                result = 2;

            return {...state, zoom: result};
        },

        zoomOut: (state, action) => {
            const pageState = current(state);
            let zoom = pageState.zoom;
            let result = 0;

            if (zoom == 2)
                result = 1.75;
            else if (zoom == 1.75)
                result = 1.5;
            else if (zoom == 1.5)
                result = 1.25;
            else if (zoom == 1.25)
                result = 1;
            else if (zoom == 1)
                result = 0.75;
            else if (zoom == 0.75)
                result = 0.5;
            else //if (zoom == 1.75)
                result = 0.25;

            return {...state, zoom: result};
        },

        //grid events handlers
        onGridColumnReorder: (state, action) => {           
            //payload {columns}
            const pageState = current(state);
            const columns = action.payload.columns;
          
            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(item => {                         
                    //change the orderId prop of every field
                    //AND then sort the fields accordingly               
                    const column = columns.find(e => e.field == item.name);
                    if (!column) //it is hidden
                        return {...item};
                    else
                        return {...item, orderId: column.orderIndex}
                }).sort((a, b) => (a.orderId > b.orderId) ? 1 : -1)
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        onGridColumnResize: (state, action) => {
            //payload {columnId, oldWidth, newWidth, end}
            const pageState = current(state);
            const columnKey = action.payload.columnId.trim();
            const columnId = columnKey.substr(1);
            const oldWidth = action.payload.oldWidth;
            const newWidth = action.payload.newWidth;
            const end = action.payload.end;

            if (!end || !pageState.currentWidget)
                return state;


            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(item => {     
                    if (item.id.toString() != columnId.toString())
                        return item;
            
                    return {...item, width: newWidth};
                })
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },
        
        onGridColumnSettingChange: (state, action) => {
            //payload {columnId, oldWidth, newWidth, end}
            const pageState = current(state);
            const fieldId = action.payload.fieldId;
            const fieldName = action.payload.fieldName;
            const prop = action.payload.prop;
            const value = action.payload.value;

            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(item => {                                        
                    if (item.id != fieldId)
                        return item;
            
                    return {...item, [prop]: value};
                })
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        onGridSort: (state, action) => {
            //payload {sort}
            const pageState = current(state);            

            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(field => {   
                    let s = action.payload.sort.find(e => e.field == field.name);

                    if (s)     
                        return {...field, isSorted: true, sortDirection: s.dir};
                    else
                        return {...field, isSorted: false, sortDirection: ""};
            })};


            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        onGridPageChange: (state, action) => {
            //payload {page}
            const pageState = current(state);            

            if (!pageState.currentWidget)
                return state;
            
            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            const page = action.payload.page;
            let skip = page.skip;
            let take = page.take;

            let currentPageIndex = 0;
            if (skip > 0)
                currentPageIndex = Math.ceil(skip / tile.properties.pageSize);

            let properties = {
                ...tile.properties, 
                currentPageIndex: currentPageIndex
            };

            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        onGridGroupChange: (state, action) => {
            //payload {group}
            const pageState = current(state);  
            let group = action.payload.group;

            if (!pageState.currentWidget)
                return state;

            let tile = pageState.tiles.find (e => e.Id == pageState.currentWidget.Id);
            if (!tile)
                return state;

            let properties = {
                ...tile.properties, 
                fields: tile.properties.fields.map(field => {   
                    let j = group.findIndex(e => e.field == field.name);
                    if ( j >= 0)
                        return ({...field, isGrouped: true, groupOrder: j});
                    else 
                        return ({...field, isGrouped: false, groupOrder: -1});
            })};

            
            return {
                ...state, 
                tiles: state.tiles.map(e => {                   
                    if (e.Id == state.currentWidget.Id)
                        return {
                            ...e, properties: properties, item: getWidgetSettingsComponent(WIDGET_GRID, properties)
                        };
                    else
                        return e;
                })
            }
        },

        /*chart to grid*/
        chartToGrid: (state, action) => {
            const pageState = current(state);

            const widgetId = action.payload;
            const tile = pageState.tiles.find(e => e.Id == widgetId);
            const dataSourceId = tile.DataSourceId;
            const mock = pageState.mocks.find(e => e.DataSourceId == dataSourceId);
            const widgetInfo = widgetsInfo.find(e => e.id == WIDGET_GRID);
            const defaultSettings = widgetInfo.defaultSettings;
            const newWidgetId = getNewWidgetId(pageState);

            let properties = {
                id: newWidgetId, widgetTypeId: WIDGET_GRID, dataSourceValueType: 0,
                mode: 1, errorText: "", loaded: true, 
                data: [...tile.properties.data], schema: [...mock.Schema], 
                currentPageIndex: 0,
                onWidgetSelect: (props, dispatch) => dispatch(selectWidget(props.id)),
                onWidgetUnSelect: (props, dispatch) => dispatch(unSelectWidget()),
                getContextMenu: props => getWidgetDesignContextMenu(WIDGET_GRID),// widgetDesignContextMenu,
                onMenuItemClick: handleOnWidgetDesignContextMenuClick,   
                onDoubleClick: (id, props, dispatch) => dispatch(showSettingsForm(id)),                 
                ...defaultSettings,                    
            };
                    
            //set properties.fields
            properties.fields = mock.Schema.map( s => {
                let obj = {
                    id: s.Id, name: s.Name, title: s.Title, 
                    datasetFieldId: s.FieldId, dataTypeId: s.DataTypeId, position: s.Position,
                    isVariation: s.IsVariation, isVariationReverse: s.IsVariationReverse,
                    ...defaultGridFieldSettings                     
                };

                if (s.DataTypeId == 1)
                {
                    obj.width = 100;
                    obj.hAlign = 2;       
                    obj.filter = "boolean";                     
                }
                else if (s.DataTypeId == 2)
                {
                    obj.width = 120;
                    obj.hAlign = 2;
                    obj.filter = "numeric";

                    obj.thousandSeparator = 1;

                    if (s.IsDecimal)
                        obj.decimalPlaces = 2;
                    else
                        obj.decimalPlaces = 0;

                    if (s.IsPercent)
                        obj.showPercent = true;
                    else 
                        obj.showPercent = false;
                }
                else if (s.DataTypeId == 3)
                {
                    obj.width = 150;
                    obj.hAlign = 1;    
                    obj.filter = "text";                        
                }
                else  if (s.DataTypeId == 4)
                {
                    obj.width = 130;
                    obj.hAlign = 2; 
                    obj.filter="date";
                    obj.dateFormat = 7;                           
                }
                else  if (s.DataTypeId == 5)
                {
                    obj.width = 160;
                    obj.hAlign = 2; 
                    obj.filter="date";
                    obj.dateFormat = 8;                           
                }
                

                return obj;
            });
                

            //events handlers
            properties.onColumnReorder= (dispatch, widgetId, columns) => {
                dispatch(selectWidget(widgetId));

                dispatch(onGridColumnReorder({columns: columns}));
            };

            properties.onColumnResize= (dispatch, widgetId, columnId, oldWidth, newWidth, columns, end) => {
                if (!end)
                    return;
                
                dispatch(selectWidget(widgetId));
                
                dispatch(onGridColumnResize({columnId: columnId, oldWidth: oldWidth, newWidth: newWidth, end: end}));
            };
            
            properties.onColumnSettingChange= (dispatch, widgetId, fieldId, fieldName, prop, value) => {
                dispatch(selectWidget(widgetId));

                dispatch(onGridColumnSettingChange({fieldId: fieldId, fieldName: fieldName, prop: prop, value: value}));
            };

            properties.onSort= (dispatch, widgetId, sort) => {
                dispatch(selectWidget(widgetId));

                dispatch(onGridSort({sort: sort}));
            };

            properties.onPageChange = (dispatch, widgetId, page) => {
                dispatch(selectWidget(widgetId));

                dispatch(onGridPageChange({page: page}));
            };

            properties.onGroupChange = (dispatch, widgetId, group) => {
                dispatch(selectWidget(widgetId));

                dispatch(onGridGroupChange({group: group}));
            };                 


            let newTile = { 
                Id: newWidgetId, WidgetTypeId: WIDGET_GRID, DatasetId: mock.DatasetId, 
                DataSourceId: dataSourceId, DataSourceTypeId: mock.DataSourceTypeId, 
                DataSourceValueTypeId: 0, 
                reorderable: properties.reorderable, resizable: properties.resizable,               
                defaultPosition:{...widgetInfo.defaultPosition, col:1, order: 1},
                properties: properties,
                item: getWidgetSettingsComponent(WIDGET_GRID, properties),
            };

            return {
                ...state,
                tiles:[...state.tiles.map(e => ({...e, defaultPosition: {...e.defaultPosition, order: e.defaultPosition.order+1}})), newTile]
            };
        },

        gridToChart: (state, action) => {
            const pageState = current(state);

            //make sure settings form is not already open
            if (pageState.settingsFormDialogOpen)
                return state;

            const id = action.payload.id;
            const tile = pageState.tiles.find(e => e.Id == id);
            const dataset = pageState.datasets.find(e => e.Id == tile.DatasetId);
            const mock = pageState.mocks.find(e => e.DataSourceId == tile.DataSourceId);

            const widgetTypeId = action.payload.widgetTypeId;
            let widgetInfo = widgetsInfo.find(e => e.id == widgetTypeId);
            let defaultSettings = widgetInfo.defaultSettings;
            let newWidgetId = getNewWidgetId(pageState);//a negative auto decrement long that guarantees no 2 widgets in new mode have the same id

            const fields = mock.Schema.map(e => toCamel(e));
            fields.splice(0, 0, {id: 0, title: "None", name: "None"});

            return {
                ...state, 
                currentWidget: null,
                settingsFormDialogOpen: true,
                settingsForm:{
                    widgetId: newWidgetId, widgetTypeId: widgetTypeId, mode: 1, 
                    dataSourceTypeId: widgetInfo.dataSourceType, dataSourceValueTypeId: widgetInfo.dataSourceValueType,  
                    dataSources: [defaultDataSource, { Id: mock.DataSourceId, Title: mock.DataSourceTitle, TypeId: 2, ValueTypeId:0, Description: ""} ],
                    pageIndex: 1,
                    formKey:(new Date()).toString(),
                    formValues: {
                        id: newWidgetId, widgetTypeId: widgetTypeId, 
                        datasetId: dataset.Id, dataSourceId: mock.DataSourceId, 
                        settings:{...defaultSettings, fields: fields}
                    },
                    formInitialValues: {
                        id: newWidgetId, widgetTypeId: widgetTypeId, 
                        datasetId: dataset.Id, dataSourceId: mock.DataSourceId, 
                        settings:{...defaultSettings, fields: fields}
                    }
                }
            };
        },


        autoWidgetCreated: (state, action) => {
            const pageState = current(state);

            const widgetTypeId = action.payload.widgetTypeId;
            const mock = action.payload.mock;

            //make sure settings form is not already open
            if (pageState.settingsFormDialogOpen)
                return state;
            
            let widgetInfo = widgetsInfo.find(e => e.id == widgetTypeId);
            let defaultSettings = widgetInfo.defaultSettings;
            let newWidgetId = getNewWidgetId(pageState);//a negative auto decrement long that guarantees no 2 widgets in new mode have the same id


            let properties = {
                id: newWidgetId, widgetTypeId: widgetTypeId, dataSourceValueType: mock.DataSourceValueTypeId,
                mode: 1, errorText: "", loaded: true, 
                data: mock.DataSourceTypeId == 2 ? [...mock.Table] : mock.Value, schema: [...mock.Schema], 
                onWidgetSelect: (props, dispatch) => dispatch(selectWidget(props.id)),
                onWidgetUnSelect: (props, dispatch) => dispatch(unSelectWidget()),
                getContextMenu: props => getWidgetDesignContextMenu(widgetTypeId),// widgetDesignContextMenu,
                onMenuItemClick: handleOnWidgetDesignContextMenuClick,   
                onDoubleClick: (id, props, dispatch) => dispatch(showSettingsForm(id)),                 
                ...defaultSettings,                    
            };

            if (widgetTypeId == WIDGET_GRID)
            {
                //set properties.fields
                properties.currentPageIndex = 0;

                properties.fields = mock.Schema.map( s => {
                    let obj = {
                        id: s.Id, name: s.Name, title: s.Title, 
                        datasetFieldId: s.FieldId, dataTypeId: s.DataTypeId, position: s.Position,
                        isVariation: s.IsVariation, isVariationReverse: s.IsVariationReverse,
                        ...defaultGridFieldSettings                     
                    };

                    if (s.DataTypeId == 1)
                    {
                        obj.width = 100;
                        obj.hAlign = 2;       
                        obj.filter = "boolean";                     
                    }
                    else if (s.DataTypeId == 2)
                    {
                        obj.width = 120;
                        obj.hAlign = 2;
                        obj.filter = "numeric";

                        obj.thousandSeparator = 1;

                        if (s.IsDecimal)
                            obj.decimalPlaces = 2;
                        else
                            obj.decimalPlaces = 0;

                        if (s.IsPercent)
                            obj.showPercent = true;
                        else 
                            obj.showPercent = false;
                    }
                    else if (s.DataTypeId == 3)
                    {
                        obj.width = 150;
                        obj.hAlign = 1;    
                        obj.filter = "text";                        
                    }
                    else  if (s.DataTypeId == 4)
                    {
                        obj.width = 130;
                        obj.hAlign = 2; 
                        obj.filter="date";
                        obj.dateFormat = 7;                           
                    }
                    else  if (s.DataTypeId == 5)
                    {
                        obj.width = 160;
                        obj.hAlign = 2; 
                        obj.filter="date";
                        obj.dateFormat = 8;                           
                    }

                    return obj;
                });
                    

                //events handlers
                properties.onColumnReorder= (dispatch, widgetId, columns) => {
                    dispatch(selectWidget(widgetId));

                    dispatch(onGridColumnReorder({columns: columns}));
                };

                properties.onColumnResize= (dispatch, widgetId, columnId, oldWidth, newWidth, columns, end) => {
                    if (!end)
                        return;
                    
                    dispatch(selectWidget(widgetId));
                    
                    dispatch(onGridColumnResize({columnId: columnId, oldWidth: oldWidth, newWidth: newWidth, end: end}));
                };

                properties.onColumnSettingChange= (dispatch, widgetId, fieldId, fieldName, prop, value) => {
                    dispatch(selectWidget(widgetId));

                    dispatch(onGridColumnSettingChange({fieldId: fieldId, fieldName: fieldName, prop: prop, value: value}));
                };

                properties.onSort= (dispatch, widgetId, sort) => {
                    dispatch(selectWidget(widgetId));

                    dispatch(onGridSort({sort: sort}));
                };

                properties.onPageChange = (dispatch, widgetId, page) => {
                    dispatch(selectWidget(widgetId));

                    dispatch(onGridPageChange({page: page}));
                };

                properties.onGroupChange = (dispatch, widgetId, group) => {
                    dispatch(selectWidget(widgetId));

                    dispatch(onGridGroupChange({group: group}));
                };                 

            }
            else if (widgetTypeId == WIDGET_TICKER)
            {
                properties.fields = mock.Schema.map(e => ({...toCamel(e), isVisible: true}));
            }
            else if (isChart(widgetTypeId))
            {
                //fields = mock.Schema.map(e => ({...toCamel(e)}));
                //fields.splice(0, 0, {id: 0, title: "None", name: "None"});
            }

            let newTile = { 
                Id: newWidgetId, WidgetTypeId: widgetTypeId, DatasetId: mock.DatasetId, 
                DataSourceId: mock.DataSourceId, DataSourceTypeId: mock.DataSourceTypeId, 
                DataSourceValueTypeId: 0, 
                reorderable: properties.reorderable, resizable: properties.resizable,               
                defaultPosition:{...widgetInfo.defaultPosition, col:1, order: 1},
                properties: properties,
                item: getWidgetSettingsComponent(widgetTypeId, properties),
            };

            return {
                ...state,
                tiles:[...state.tiles.map(e => ({...e, defaultPosition: {...e.defaultPosition, order: e.defaultPosition.order+1}})), newTile]
            };
        },


        setContainsWidgetsNonDeepChanges: (state, action) => ({...state, containsWidgetsNonDeepChanges: action.payload}),

        setContainsWidgetsDeepChanges: (state, action) => ({...state, containsWidgetsDeepChanges: action.payload}),

        setContainsGlobalChanges: (state, action) => ({...state, containsGlobalChanges: action.payload}),


        disposePage: (state, action) => {
            //payload: bool  
            if (action.payload)
                return {...initialState};
            else  
                return {...state, pageDisposed: false};
        },
    }
});

export default reportingDesignPageSlice.reducer;


export const loadNewReportPage = (t) => async (dispatch, getState) => {    
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.preparingWorkspace")}));

    try{
        var response = await api({            
                url: 'reportingdesign/getreportingdesignpagedatanew',
                method:  'post',
                data: {
                   
                },
                timeout: SITE_CONNECTION_TIMEOUT_LONG
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = {...response.data.Data, lastModifiedOn: ""};

        //transform the response
        //add the 0/None dataset
        data.Datasets.splice(0, 0, {...defaultDataset});
       
        dispatch(newReportPageLoaded({...data, currentUser: {...getState().currentUser}}));

        dispatch(setCurrentPage({id: "reportingDesignPage", isDirty: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const loadExistingReportPage = (referenceKey, t) => async (dispatch, getState) => {    
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText:  t("slices.loadingPage")}));

    try{
        var response = await api({            
                url: 'reportingdesign/getreportingdesignByReferenceKey',
                method:  'post',
                data: {
                   referenceKey
                },
                timeout: SITE_CONNECTION_TIMEOUT_VERY_LONG
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;

        //transform the response        
        //add the 0/None dataset
        data.Datasets.splice(0, 0, {...defaultDataset});

        dispatch(existingReportPageLoaded({data: data, currentUser: getState().currentUser}));

        dispatch(setCurrentPage({id: "reportingDesignPage", isDirty: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const loadDatasetDataSources = (datasetId, t) => async (dispatch, getState) => {    
    const pageState = getState().reportingDesignPage;
    const dataSourceTypeId = pageState.settingsForm.dataSourceTypeId;
    const dataSourceValueTypeId = pageState.settingsForm.dataSourceValueTypeId;
    const isDate = dataSourceValueTypeId == 4 || dataSourceValueTypeId == 5;

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingDataSources")}));
    
    if (datasetId == 0)
    {
        dispatch(datasetDataSourcesLoaded({datasetId: datasetId, dataSources: [defaultDataSource], aggregateExpressions: [defaultAggregateExpression]}));
        return Promise.resolve(response);
    }

    try{
        var response = await api({            
                url: 'datasource/getdatasourcesbydatasetid',
                method:  'post',
                data: {
                   datasetId: datasetId,
                   includeImplicit: false
                },
                timeout: SITE_CONNECTION_TIMEOUT_LONG
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;

        //transform the response
        //add the 0/None dataset
        let dataSources = null;
        let aggregateExpressions = [];

        if (dataSourceValueTypeId && dataSourceValueTypeId > 0)
        {
            //aggregate expressions need to be filled
            if (isDate)
            {
                dataSources = data.Items.filter(e => e.TypeId == dataSourceTypeId && (e.ValueTypeId == 4 || e.ValueTypeId == 5));
                aggregateExpressions = data.Fields.filter(e => (e.DataTypeId == 4 || e.DataTypeId == 5) && e.IsExpression==true && e.IsAggregate == true);
            }
            else
            {
                //numeric values 
                dataSources = data.Items.filter(e => e.TypeId == dataSourceTypeId && e.ValueTypeId == dataSourceValueTypeId);
                aggregateExpressions = data.Fields.filter(e => e.DataTypeId == dataSourceValueTypeId && e.IsExpression == true && e.IsAggregate == true);
            }
        }
        else
            dataSources = data.Items.filter(e => e.TypeId==dataSourceTypeId);

        dataSources.splice(0, 0, {...defaultDataset});

        aggregateExpressions.splice(0, 0, {...defaultAggregateExpression});

        dispatch(datasetDataSourcesLoaded({datasetId: datasetId, dataSources: dataSources, aggregateExpressions: aggregateExpressions}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const loadDataSourceFields = (dataSourceId, t) => async (dispatch, getState) => {    
    debugger;
    //i get here only if the widget is a chart or ticker => i need to load the data source fields to
    //select from them if chart(category field, value field etc.) or to show/hide if ticker
    const pageState = getState().reportingDesignPage;
    let fields = [];

    //test if the data source is in mock => fetch the fields locally
    let mock = pageState.mocks.find(e => e.DataSourceId == dataSourceId);
    if (mock)
    {
        if (isChart(pageState.settingsForm.widgetTypeId))
        {
            fields = [...mock.Schema]
            .map(item => ({id: item.Id, datasetFieldId: item.FieldId, name: item.Name, title: item.Title, dataTypeId: item.DataTypeId, orderId: item.OrderId}))
            .sort((a,b) => (a.title <= b.title) ? -1 : 1) ; 
            
            fields.splice(0, 0, {id: 0, name: "none", title: "None", dataTypeId: 2, orderId: -100, datasetFieldId: null});
        }
        else if (pageState.settingsForm.widgetTypeId == WIDGET_TICKER)
        {
            fields = [...mock.Schema]//already sorted by title from api
            .map((item, index) => ({id: item.Id, datasetFieldId: item.FieldId, name: item.Name, title: item.Title, dataTypeId: item.DataTypeId, orderId: index, isVisible: true})); 
        }

        //chartDataSourceSelected does the same as dataSourceReset() but with the newly loaded fields => no need to call dataSourceReset()
        dispatch(chartDataSourceSelected({dataSourceId: dataSourceId, fields: fields}));

        return;
    }

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingDataSourceFields")}));
    
    try{
        var response = await api({            
                url: 'datasource/getdatasourcebyid',
                method:  'post',
                data: {
                   id: dataSourceId
                },
                timeout: SITE_CONNECTION_TIMEOUT_LONG
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;
       
        //api returns the fields sorted by position not title
        if (isChart(pageState.settingsForm.widgetTypeId))
        {
            fields = data.Fields
                        .map(item => ({id: item.Id, datasetFieldId: item.FieldId, name: item.Name, title: item.Title, dataTypeId: item.DataTypeId, orderId: item.OrderId}))
                        .sort((a,b) => (a.title <= b.title) ? -1 : 1) ;

            fields.splice(0, 0, {id: 0, name: "none", title: "None", dataTypeId: 2, orderId: -100});
        }
        else if (pageState.settingsForm.widgetTypeId == WIDGET_TICKER)
        {
            fields = data.Fields
                //.sort((a, b) => (a.Title > b.Title) ? 1 : -1)
                .map((item, index) => ({id: item.Id, datasetFieldId: item.FieldId, name: item.Name, title: item.Title, dataTypeId: item.DataTypeId, orderId: index, isVisible: true})); 
        }

        //chartDataSourceSelected does the same as dataSourceReset() but with the newly loaded fields => no need to call dataSourceReset()
        dispatch(chartDataSourceSelected({dataSourceId: dataSourceId, fields: fields}));

        return Promise.resolve({dataSourceId: dataSourceId, fields: fields});
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const loadDataSourceMock = (dataSourceId, t) => async (dispatch, getState) => {    
    //called when user clicks Save in settongs form and the data is not in mock => call here to get data source mock data from api
    const pageState = getState().reportingDesignPage;

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingMockData")}));
    try{
        var response = await api({            
                url: 'datasource/getdatasourcemock',
                method:  'post',
                data: {
                   id: dataSourceId
                },
                timeout: SITE_CONNECTION_TIMEOUT_VERY_LONG
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;


        //transform the response
        if (data.Table)
            data.Table = JSON.parse(data.Table);

        dispatch(dataSourceMockLoaded({dataSourceId: dataSourceId, mock: data}));

        dispatch(settingsFormSave(data));

        return Promise.resolve({dataSourceId: dataSourceId, mock: data});
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const getOrCreateImplicitValueDataSource = (aggregateExpressionId, t) => async (dispatch, getState) => {    
    //called when user clicks Save in settongs form and the data is not in mock => call here to get data source mock data from api
    const pageState = getState().reportingDesignPage;

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingMockData")}));
    
    try{
        var response = await api({            
                url: 'datasource/getOrcreateimplicitvaluedatasource',
                method:  'post',
                data: {
                   expressionId: aggregateExpressionId
                },
                timeout: SITE_CONNECTION_TIMEOUT_VERY_LONG
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;

        //transform the response
        dispatch(implicitDataSourceLoaded({aggregateExpressionId: aggregateExpressionId, mock: data}));

        dispatch(settingsFormSave(data));

        return Promise.resolve({aggregateExpressionId: aggregateExpressionId, mock: data});
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const removeWidget = (id) => async (dispatch, getState) => {    
    const pageState = getState().reportingDesignPage;

    dispatch(setAppDialog({
        isVisible: true,
        className: "reporting-design-page-are-you-sure-widget",
        title: i18next.t("slices.warning"), content: i18next.t("slices.areyousure"),
        actions:{
            yesButton:{ title: i18next.t("slices.yes"), handler: () =>{
                dispatch(hideAppDialog());

                dispatch(widgetRemoved(id));
            }},
            noButton: {title: i18next.t("slices.no")}
        }
    }));
};

export const createEntity = (t) => async (dispatch, getState) => {
    let pageState = getState().reportingDesignPage;

    dispatch(setProcessing({processing: true, processingText: t("slices.creatingReport")}));

    //transform the data
    let obj = buildReportObject(pageState);
  
    try{
        var response = await api({            
            url:"/reportingdesign/createreport",
            method:  'post',
            data: {...obj},
            timeout: SITE_CONNECTION_TIMEOUT_LONG
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;

        let data = response.data.Data;

        return Promise.resolve({referenceKey: data.ReferenceKey});
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const updateEntity = (saveAsDefault=false, t) => async (dispatch, getState) => {
    let pageState = getState().reportingDesignPage;
    dispatch(setProcessing({processing: true, processingText: saveAsDefault ? t("slices.savingDefault") : t("slices.updatingReport")}));

    //transform the data
    let obj = buildReportObject(pageState);

    try{
        var response = await api({            
            url:"/reportingdesign/updatereport",
            method: 'post',
            data: {
                ...obj,
                saveAsDefault: saveAsDefault
            },
            timeout: SITE_CONNECTION_TIMEOUT_VERY_LONG
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(entityUpdated({...data}));

        dispatch(addNotification({text: t("slices.updatedReport"), type: "success", closable: true, duration:3, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        dispatch(setCurrentPage({id: "reportingDesignPage", isDirty: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const revertToDefault = (t) => async (dispatch, getState) => {
    let pageState = getState().reportingDesignPage;
    dispatch(setProcessing({processing: true, processingText: t("slices.reverting")}));

    //transform the data
    let obj = buildReportObject(pageState);

    try{
        var response = await api({            
            url:"/reportingdesign/reverttodefaultreport",
            method: 'post',
            data: {reportId: pageState.reportId},
            timeout: SITE_CONNECTION_TIMEOUT_LONG
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(addNotification({text: t("slices.revertCompleted"), type: "success", closable: true, duration:3, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const deleteEntity = (referenceKey, t) => async (dispatch, getState) => {
    //payload the entity id
    dispatch(setProcessing({processing: true, processingText: t("slices.deletingReport")}));

    try{
        var response = await api({            
            url:"/reportingdesign/removereport",
            method:  'post',
            data: {referenceKey: referenceKey},
            timeout: SITE_CONNECTION_TIMEOUT_LONG
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().reportingDesignPage.pageDisposed)
           return;

        let data = response.data.Data;

        let currentUser = getState().currentUser;

        dispatch(setCurrentUser({...currentUser, ReportsMenu: currentUser.ReportsMenu.filter(e => e.referenceKey != referenceKey)}))
        dispatch(addNotification({text: t("slices.deletedReport"), type: "success", closable: true, duration:3, position:"topRight"}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const createAutoWidget = (widgetTypeId, dataSourceName, t) => async (dispatch, getState) => {
    //payload the entity id
    dispatch(setProcessing({processing: true, processingText: t("slices.creatingWidget")}));

    let pageState = getState().reportingDesignPage;
    dataSourceName = dataSourceName.trim().toLowerCase();

    //try fetching the data source from mock
    let mock = pageState.mocks.find(e => e.DataSourceTitle.trim().toLowerCase() == dataSourceName);
    let widgetInfo = null;

    widgetInfo = widgetsInfo.find(e => e.id == widgetTypeId);
    if (!widgetInfo)
        return Promise.resolve();

    if (mock)
    {
        //make sure the data source type matches
        if (widgetInfo.dataSourceValueType == 4 || widgetInfo.dataSourceValueType == 5)
        {
            if (mock.DataSourceTypeId != widgetInfo.dataSourceType || (mock.DataSourceValueTypeId != 4 && mock.DataSourceValueTypeId != 5))
                return Promise.reject(t("slices.invalidOperation"));
        }
        else if (widgetInfo.dataSourceType != mock.DataSourceTypeId || widgetInfo.dataSourceValueType != mock.DataSourceValueTypeId)
            return Promise.reject(t("slices.invalidOperation"));

        dispatch(autoWidgetCreated({widgetTypeId: widgetTypeId, mock: mock}));
        return Promise.resolve();
    }
    else
    {
        try{
            var response = await api({            
                    url: 'datasource/getdatasourcemock',
                    method:  'post',
                    data: {
                       id: 0,
                       title: dataSourceName
                    },
                    timeout: SITE_CONNECTION_TIMEOUT_VERY_LONG
                    //headers:{'h':'1'}
            });
            
            //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
            if(getState().reportingDesignPage.pageDisposed)
               return;
               
            //it's ok to dispatch here
            let data = response.data.Data;    
    
            //transform the response
            if (data.Table)
                data.Table = JSON.parse(data.Table);
    
            //save the mock in mocks anyway
            dispatch(dataSourceMockLoaded({dataSourceId: data.DataSourceId, mock: data}));
    
            mock = data;

            if (widgetInfo.dataSourceValueType == 4 || widgetInfo.dataSourceValueType == 5)
            {
                if (mock.DataSourceTypeId != widgetInfo.dataSourceType || (mock.DataSourceValueTypeId != 4 && mock.DataSourceValueTypeId != 5))
                    return Promise.reject(t("slices.invalidOperation"));
            }
            else if (widgetInfo.dataSourceType != mock.DataSourceTypeId || widgetInfo.dataSourceValueType != mock.DataSourceValueTypeId)
                return Promise.reject(t("slices.invalidOperation"));
    
            dispatch(autoWidgetCreated({widgetTypeId: widgetTypeId, mock: mock}));
            return Promise.resolve();
        }
        catch(err)
        {
            //i am here => could be any error included 200 and Succeeded=false        
            return Promise.reject(err);
        }
    }
};


export const { 
    setInitialState, setProcessing, setPageValue, pageLoaded, setPanes, setSettingsFormValue, setSettingsFormOuterValue, setSettingsFormSettingValue, disposePage, newReportPageLoaded, existingReportPageLoaded, datasetDataSourcesLoaded, dataSourceReset, showSettingsFormNew, closeSettingsForm, settingsFormPrevious, settingsFormNext, dataSourceMockLoaded, settingsFormSave, selectWidget, unSelectWidget, showSettingsForm, widgetRemoved, entityUpdated,
    onGridColumnResize, onGridColumnSettingChange, onGridSort, onGridPageChange, onGridGroupChange, onGridColumnReorder, chartDataSourceSelected, resetTileWidget,
    zoomIn, zoomOut, chartToGrid, gridToChart, autoWidgetCreated, implicitDataSourceLoaded, redrawTiles,
    setContainsWidgetsNonDeepChanges, setContainsWidgetsDeepChanges, setContainsGlobalChanges, redirectToSchedule
 } = reportingDesignPageSlice.actions;
